import type { DocumentDto } from '@/types/document';

export const SOURCE_FILTER_OPTIONS = [
  { value: 'upload', label: 'Upload' },
  { value: 'sharepoint', label: 'SharePoint' },
  { value: 'filesystem', label: 'Filesystem' },
  { value: 'erp', label: 'ERP' },
  { value: 'connector', label: 'Alle Connectoren' },
] as const;

const LANGUAGE_LABELS: Record<string, string> = {
  de: 'Deutsch',
  en: 'Englisch',
  fr: 'Franzoesisch',
  es: 'Spanisch',
  it: 'Italienisch',
  pl: 'Polnisch',
  nl: 'Niederlaendisch',
};

const ACTIVE_STATUSES = new Set(['Completed', 'CompletedWithoutEmbeddings']);
const PROCESSING_STATUSES = new Set(['Uploaded', 'Parsing', 'Parsed', 'Extracting']);
const REVIEW_STATUSES = new Set(['AwaitingConfirmation']);
const PROBLEM_STATUSES = new Set(['Failed', 'PartiallyFailed', 'EmptyDocument', 'Deleted']);

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatDateTime(iso?: string | null): string {
  if (!iso) return 'Nicht verfuegbar';

  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getDocumentDisplayTitle(document: Pick<DocumentDto, 'displayTitle' | 'fileName'>): string {
  const normalizedTitle = document.displayTitle?.trim();
  return normalizedTitle && normalizedTitle.length > 0 ? normalizedTitle : document.fileName;
}

export function formatPageCountLabel(pageCount?: number | null): string {
  if (!pageCount || pageCount <= 0) {
    return 'Nicht persistiert';
  }

  return `${pageCount} Seite${pageCount === 1 ? '' : 'n'}`;
}

export function formatVersionLabel(document: Pick<DocumentDto, 'versionNumber' | 'versionLabel' | 'isCurrent'>): string {
  const base = `V${document.versionNumber}`;
  const labeled = document.versionLabel ? `${base} (${document.versionLabel})` : base;
  return document.isCurrent ? `${labeled} · Aktuell` : `${labeled} · Ersetzt`;
}

export function formatChunkProgress(document: Pick<DocumentDto, 'processedChunks' | 'totalChunks' | 'failedChunks'>): string {
  if (document.totalChunks <= 0) {
    return 'Noch keine Chunk-Daten';
  }

  if (document.failedChunks > 0) {
    return `${document.processedChunks}/${document.totalChunks} verarbeitet · ${document.failedChunks} fehlgeschlagen`;
  }

  return `${document.processedChunks}/${document.totalChunks} verarbeitet`;
}

export function isActiveDocumentStatus(status: string): boolean {
  return ACTIVE_STATUSES.has(status);
}

export function isProcessingDocumentStatus(status: string): boolean {
  return PROCESSING_STATUSES.has(status);
}

export function isReviewDocumentStatus(status: string): boolean {
  return REVIEW_STATUSES.has(status);
}

export function isProblemDocumentStatus(status: string): boolean {
  return PROBLEM_STATUSES.has(status);
}

export function isConnectorSource(sourceKind?: string | null): boolean {
  return !!sourceKind && sourceKind !== 'upload';
}

export function getSourceKindLabel(sourceKind?: string | null): string {
  switch ((sourceKind ?? '').toLowerCase()) {
    case 'sharepoint':
      return 'SharePoint';
    case 'filesystem':
      return 'Filesystem';
    case 'erp':
      return 'ERP';
    case 'connector':
      return 'Connector';
    case 'upload':
    default:
      return 'Upload';
  }
}

export function getUploaderLabel(document: Pick<DocumentDto, 'uploadedByDisplayName' | 'sourceKind'>): string {
  if (document.uploadedByDisplayName) {
    return document.uploadedByDisplayName;
  }

  return isConnectorSource(document.sourceKind) ? 'Connector / System' : 'Unbekannt';
}

export function formatSourceOrigin(document: Pick<DocumentDto, 'sourceKind' | 'sourceOrigin'>): string {
  const normalizedOrigin = document.sourceOrigin?.trim();
  if (normalizedOrigin) {
    return normalizedOrigin;
  }

  return isConnectorSource(document.sourceKind)
    ? 'Nicht verfuegbar'
    : 'Nicht vorhanden (manueller Upload)';
}

export function formatDocumentTypeLabel(documentType?: string | null): string {
  if (!documentType) {
    return 'Nicht klassifiziert';
  }

  return documentType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (value) => value.toUpperCase());
}

export function formatLanguageLabel(language?: string | null): string {
  if (!language) {
    return 'Nicht gesetzt';
  }

  const normalized = language.trim().toLowerCase();
  return LANGUAGE_LABELS[normalized] ?? normalized.toUpperCase();
}